import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Navbar from '../Components/Navbar'
import CartItemCard from '../Components/CartItemCard'
import PaymentPanel from '../Components/PaymentPanel'
import { useCart } from '../Contexts/CartContext'
import '../Styles/MyCart.scss'

// The cart page. Everything it displays comes from /cart/quote, and every interaction re-quotes —
// there is no price arithmetic anywhere in this file.
//
// Paying is a single server call. /cart/pay prices the cart, re-checks spots, stamps the agreed
// per-camper amounts onto the rows, creates the PaymentIntent and confirms it, all in one request.
// Nothing here sends an amount; this file sends which rows to pay for and a ConfirmationToken.
function MyCart() {

   const navigate = useNavigate()
   const { refreshCount } = useCart()

   const [lines, setLines] = useState([])
   const [orderTotal, setOrderTotal] = useState(0)
   const [remainingTotal, setRemainingTotal] = useState(0)
   const [selection, setSelection] = useState(null)
   const [loading, setLoading] = useState(true)
   const [busy, setBusy] = useState(false)
   const [error, setError] = useState('')
   const [healed, setHealed] = useState(false)
   const [isPaying, setIsPaying] = useState(false)
   // The cart_item_id waiting on a yes/no, not yet sent to the server — Remove always asks first.
   const [pendingRemoveId, setPendingRemoveId] = useState(null)

   const asDollars = (cents) => (cents / 100).toFixed(2)

   const applyQuote = useCallback((data) => {
      setLines(data.lines ?? [])
      setOrderTotal(data.order_total_cents ?? 0)
      setRemainingTotal(data.remaining_total_cents ?? 0)
      // Mirror the server's view of what is selected, so an unavailable line the server
      // force-deselected does not stay checked in the browser.
      const next = {}
      ;(data.lines ?? []).forEach(line => { next[line.cart_item_id] = line.selected })
      setSelection(next)
   }, [])

   const loadCart = useCallback(async () => {
      setLoading(true)
      try {
         const response = await fetch('/cart/list', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
         })
         const data = await response.json()
         if (!response.ok) {
            setError(data.error ?? 'Could not load your cart.')
            return
         }
         if (data.healed) {
            // A payment had already gone through for these items but never finished recording.
            setHealed(true)
         }
         applyQuote(data)
      } catch {
         setError('Could not load your cart.')
      } finally {
         setLoading(false)
         refreshCount()
      }
   }, [applyQuote, refreshCount])

   const requote = useCallback(async (nextSelection) => {
      try {
         const response = await fetch('/cart/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selection: nextSelection }),
         })
         const data = await response.json()
         if (response.ok) {
            applyQuote(data)
         }
      } catch {
         setError('Could not update your total.')
      }
   }, [applyQuote])

   useEffect(() => {
      loadCart()
   }, [loadCart])

   async function toggleSelected(cartItemId) {
      const next = { ...selection, [cartItemId]: !selection[cartItemId] }
      setSelection(next)
      await requote(next)
   }

   async function changeOption(cartItemId, paymentOption) {
      setBusy(true)
      try {
         await fetch('/cart/update-option', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItemId, paymentOption }),
         })
         await requote(selection)
      } finally {
         setBusy(false)
      }
   }

   // Returns an error message to show on the line, or nothing when it worked.
   async function applyPromo(cartItemId, promoCode) {
      setBusy(true)
      try {
         const response = await fetch('/cart/apply-promo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItemId, promoCode }),
         })
         const data = await response.json()
         if (!response.ok) {
            return data.error ?? 'Could not apply that code.'
         }
         await requote(selection)
         return ''
      } catch {
         return 'Could not apply that code.'
      } finally {
         setBusy(false)
      }
   }

   async function removePromo(cartItemId) {
      setBusy(true)
      try {
         await fetch('/cart/remove-promo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItemId }),
         })
         await requote(selection)
      } finally {
         setBusy(false)
      }
   }

   async function removeItem(cartItemId) {
      setBusy(true)
      try {
         await fetch('/cart/remove', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItemId }),
         })
         await loadCart()
      } finally {
         setBusy(false)
      }
   }

   // The Remove link on a card only ever asks; nothing is sent to the server until the dialog is
   // confirmed.
   function requestRemove(cartItemId) {
      setPendingRemoveId(cartItemId)
   }

   function cancelRemove() {
      setPendingRemoveId(null)
   }

   async function confirmRemove() {
      const cartItemId = pendingRemoveId
      setPendingRemoveId(null)
      await removeItem(cartItemId)
   }

   function goToSuccess(paymentAmountCents, registrationCount) {
      refreshCount()
      navigate('/daycamppaymentsuccess', {
         state: { paymentAmount: asDollars(paymentAmountCents), registrationCount },
      })
   }

   // Posts the token to the one endpoint that does everything. Returns the server's result to the
   // form, which owns the 3DS step because it holds the Stripe instance.
   async function pay(confirmationTokenId) {
      setError('')
      try {
         const response = await fetch('/cart/pay', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selection, confirmationTokenId }),
         })
         const data = await response.json()

         if (response.status === 409 && data.status === 'insufficient_spots') {
            // Nothing was charged. Reload so the short weeks show their real numbers.
            await loadCart()
            const detail = (data.weeks ?? [])
               .map(week => `${week.camp_name} has ${week.spots_left} spot${week.spots_left === 1 ? '' : 's'} left and your cart has ${week.in_cart}`)
               .join('; ')
            return { error: `${detail}. Update your cart, or contact the camp secretary.` }
         }

         if (!response.ok) {
            return { error: data.error ?? data.message ?? 'Could not complete your payment.' }
         }

         return data
      } catch {
         return { error: 'Could not complete your payment.' }
      }
   }

   // Payment succeeded. Either the server already finalized it, or 3DS finished and it still needs
   // recording — and if that call fails the webhook completes it, so this is never an error.
   async function handleSettled(result) {
      setIsPaying(true)

      if (result.status === 'completed_after_action') {
         try {
            const response = await fetch('/cart/finalize', {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify({ paymentIntentId: result.paymentIntentId }),
            })
            const data = await response.json()
            return goToSuccess(data.amount_paid_cents ?? orderTotal, data.registrations?.length ?? 0)
         } catch {
            setError('Payment received — your registrations will appear on your dashboard shortly.')
            setIsPaying(false)
            return
         }
      }

      // Charged, but finalize did not write the rows — a guard tripped, and the office has been
      // told. Never show a success page for registrations that do not exist.
      if (result.status !== 'completed') {
         setError('Payment received. Your registrations are being confirmed — the camp office will follow up shortly.')
         setIsPaying(false)
         return
      }

      goToSuccess(orderTotal, result.registrationIds?.length ?? selectedLines.length)
   }

   // A cart covered entirely by promos never touches Stripe, so there is no card and no token.
   async function completeFree() {
      setBusy(true)
      setError('')
      try {
         const response = await fetch('/cart/pay', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selection }),
         })
         const data = await response.json()
         if (!response.ok) {
            setError(data.error ?? 'Could not complete your registration.')
            return
         }
         goToSuccess(0, data.registrations?.length ?? 0)
      } catch {
         setError('Could not complete your registration.')
      } finally {
         setBusy(false)
      }
   }

   const selectedLines = lines.filter(l => l.selected)
   const availableLines = lines.filter(l => !l.unavailable)
   const allSelected = availableLines.length > 0 && availableLines.every(l => l.selected)

   async function toggleAll() {
      const next = { ...selection }
      availableLines.forEach(line => { next[line.cart_item_id] = !allSelected })
      setSelection(next)
      await requote(next)
   }

   return (
      <div>
         <div className="NavBar">
            <Navbar />
         </div>

         <div className="MyCartContent">
            <div className="MyCartContainer">

               <div className="MyCartHeading">
                  My Cart
                  {lines.length > 0 && <span className="MyCartCount">
                     {lines.length} registration{lines.length === 1 ? '' : 's'}
                  </span>}
               </div>

               {healed && (
                  <div className="MyCartHealedBanner">
                     Your payment went through. Your registrations are confirmed and are shown on your dashboard.
                  </div>
               )}

               {error && <div className="MyCartErrorBanner">{error}</div>}

               {loading && <div className="MyCartLoading">Loading your cart…</div>}

               {!loading && lines.length === 0 && !healed && (
                  <div className="MyCartEmpty">
                     <p>Your cart is empty.</p>
                     <Link to="/registrationcampselection">
                        <button type="button">Register for an event</button>
                     </Link>
                  </div>
               )}

               {!loading && lines.length > 0 && (
                  <>
                     {lines.some(l => l.unavailable) && (
                        <div className="MyCartUnavailableBanner">
                           Some registrations are no longer available and have been unchecked.
                           They are not included in your total.
                        </div>
                     )}

                     <div className="MyCartSelectAll">
                        <label>
                           <input type="checkbox" checked={allSelected} disabled={busy || isPaying} onChange={toggleAll} />
                           <span>
                              Select all ({selectedLines.length} of {availableLines.length} available)
                           </span>
                        </label>
                     </div>

                     <div className="MyCartList">
                        {lines.map(line => (
                           <CartItemCard
                              key={line.cart_item_id}
                              line={line}
                              busy={busy || isPaying}
                              onToggleSelected={toggleSelected}
                              onChangeOption={changeOption}
                              onApplyPromo={applyPromo}
                              onRemovePromo={removePromo}
                              onRemove={requestRemove} />
                        ))}
                     </div>

                     <div className="MyCartSummary">
                        {selectedLines.map(line => (
                           <div className="MyCartSummaryRow" key={line.cart_item_id}>
                              <div>{line.camp_name} — {line.first_name} {line.last_name}</div>
                              <div>${asDollars(line.charge_now_cents)}</div>
                           </div>
                        ))}

                        <div className="MyCartSummaryTotal">
                           <div>Due today</div>
                           <div>${asDollars(orderTotal)}</div>
                        </div>

                        {remainingTotal > 0 && (
                           <div className="MyCartSummaryRemaining">
                              <div>Remaining balance, due before camp</div>
                              <div>${asDollars(remainingTotal)}</div>
                           </div>
                        )}

                        <div className="MyCartActions">
                           {orderTotal === 0 && selectedLines.length > 0 && (
                              <button
                                 type="button"
                                 className="MyCartCheckoutButton"
                                 disabled={busy || isPaying}
                                 onClick={completeFree}>
                                 Complete registration
                              </button>
                           )}
                           <Link to="/registrationcampselection">
                              <button type="button" className="MyCartKeepShoppingButton">
                                 Register for another event
                              </button>
                           </Link>
                        </div>
                     </div>

                     {/* No "proceed to checkout" step: the form is always here, and the single
                         /cart/pay call does the spot check and the charge together. */}
                     {orderTotal > 0 && selectedLines.length > 0 && (
                        <div className="MyCartPayment">
                           <PaymentPanel
                              totalCents={orderTotal}
                              lines={selectedLines.map(line => ({
                                 key: line.cart_item_id,
                                 label: `${line.camp_name} — ${line.first_name} ${line.last_name}`,
                                 amountCents: line.charge_now_cents,
                              }))}
                              pay={pay}
                              onSettled={handleSettled}
                              onProcessingChange={setIsPaying}
                              busyLabel={isPaying ? 'Confirming your registrations…' : ''} />
                        </div>
                     )}
                  </>
               )}
            </div>
         </div>

         {pendingRemoveId !== null && (
            <div className="MyCartModalOverlay" role="presentation" onClick={cancelRemove}>
               <div
                  className="MyCartModal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="MyCartModalMessage"
                  // Stops a click on the dialog itself from bubbling to the overlay and closing it.
                  onClick={(event) => event.stopPropagation()}>
                  <div className="MyCartModalMessage" id="MyCartModalMessage">
                     Are you sure you would like to remove this registration?
                  </div>
                  <div className="MyCartModalActions">
                     <button type="button" className="MyCartModalCancel" onClick={cancelRemove}>
                        No
                     </button>
                     <button type="button" className="MyCartModalConfirm" onClick={confirmRemove}>
                        Yes
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   )
}

export default MyCart
